import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, ExternalLink, BadgeCheck, Loader2, Info } from "lucide-react";
import { SafeImg } from "./SafeImg";

// "Firmanı Sahiplen": sitede zaten listelenen işletmeleri bulup hesaba bağlama
const ClaimBusiness = ({ firm, API, token, onClaimed }) => {
  const [q, setQ] = useState(firm?.name || "");
  const [claiming, setClaiming] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCities, setAllCities] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      const term = q.trim();
      axios.get(`${API}/firms/search`, { params: { q: term || undefined, city: !allCities && firm?.city ? firm.city : undefined, limit: 24 } })
        .then(({ data }) => { if (active) setResults((data.items || []).filter((v) => v.slug !== firm?.slug)); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [q, allCities, firm, API]);

  const claim = async (venue) => {
    setClaiming(venue.slug);
    try {
      const imgs = (venue.gallery && venue.gallery.length ? venue.gallery : [venue.image]).filter(Boolean).slice(0, 6);
      await axios.post(
        `${API}/firms/me/claim-request`,
        {
          venueSlug: venue.slug,
          venueName: venue.name,
          category: venue.category,
          city: venue.city,
          district: venue.district || "",
          capacity: venue.capacity || "",
          priceFrom: venue.priceFrom || 0,
          description: venue.description || "",
          features: venue.features || [],
          images: imgs,
        },
        { headers }
      );
      onClaimed && onClaimed(venue, null, true);
    } catch (e) {
      onClaimed && onClaimed(null, e?.response?.data?.detail || "Sahiplenme talebi gönderilemedi.");
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8">
      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#F6E4E6] flex items-center justify-center shrink-0">
            <BadgeCheck className="w-6 h-6 text-[#9E2A3B]" />
          </div>
          <div>
            <h2 className="font-display text-xl text-[#2A2224]">Firmanı Sahiplen</h2>
            <p className="text-sm text-[#7C6E70] mt-1">
              İşletmen sitemizde zaten listeleniyorsa aşağıdan bul ve sahiplenme talebi gönder. Talebin patron onayına düşer; gerekli kontrollerden sonra onaylanınca ilan; fotoğrafları, yorumları ve görüntülenmeleriyle birlikte hesabına devredilir.
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 px-3 rounded-xl bg-[#FBF5F0] border border-transparent focus-within:border-[#E7A9B0] transition-colors">
          <Search className="w-5 h-5 text-[#C15B6B]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="claim-search-input"
            placeholder="İşletme adıyla ara..."
            className="w-full bg-transparent h-12 outline-none text-[#2A2224] placeholder:text-[#a8999b]"
          />
        </div>
        {firm?.city && (
          <p className="mt-2 text-xs text-[#a8999b] flex items-center gap-1 flex-wrap">
            <Info className="w-3.5 h-3.5" /> {allCities ? "Tüm illerdeki listeler gösteriliyor." : `${firm.city} ilindeki listeler gösteriliyor.`}
            <button type="button" onClick={() => setAllCities((v) => !v)} data-testid="claim-toggle-cities" className="text-[#9E2A3B] font-medium underline-offset-2 hover:underline ml-1">
              {allCities ? `Sadece ${firm.city}` : "Tüm illerde ara"}
            </button>
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 bg-white rounded-2xl border border-[#F0DDE0] text-[#7C6E70]" data-testid="claim-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#C15B6B]" /> İşletmeler aranıyor...
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#F0DDE0]">
          <p className="font-display text-xl text-[#2A2224]">Eşleşen işletme bulunamadı</p>
          <p className="text-[#7C6E70] mt-2">Farklı bir arama deneyin veya panelden bilgilerinizi elle girin.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {results.map((v) => (
            <div key={v.slug} data-testid={`claim-result-${v.slug}`} className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden flex flex-col">
              <div className="h-40 overflow-hidden">
                <SafeImg src={v.image} alt={v.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-display text-lg text-[#2A2224] line-clamp-1">{v.name}</h3>
                <p className="flex items-center gap-1 text-sm text-[#7C6E70] mt-1">
                  <MapPin className="w-3.5 h-3.5 text-[#C15B6B]" /> {[v.district, v.city].filter(Boolean).join(", ")}
                </p>
                <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-[#F0DDE0]">
                  <Link
                    to={`/mekan/${v.slug}`}
                    target="_blank"
                    className="flex items-center justify-center gap-1.5 w-full h-10 rounded-xl border border-[#E7A9B0] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBF0F2] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> İlana Git
                  </Link>
                  <button
                    onClick={() => claim(v)}
                    data-testid={`claim-submit-${v.slug}`}
                    disabled={claiming === v.slug}
                    className="flex items-center justify-center gap-1.5 w-full h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70"
                  >
                    {claiming === v.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
                    Sahiplenme Talebi Gönder
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClaimBusiness;
